import { call, put, select, takeEvery } from 'redux-saga/effects';
import { failurePut } from '../../app/failurePut';
import { deleteRequest, getRequest, postRequest } from '../../app/request';
import {
    IDeleteDepositFormUserDataActionPayload,
    IDepositAvailableMethod,
    IDepositAvailableMethods,
    IDepositCreateActionPayload,
    IDepositCreateSuccessPayload,
    IDepositFormDataListItem,
    IDepositFormUserDataResponse,
    IDepositParams,
    IGetDepositAvailableMethodsActionPayload,
    IGetDepositFormUserDataActionPayload,
    IGetDepositSurveyActionPayload,
    ISendDepositSurveyActionPayload,
    ISetDepositFormUserDataActionPayload,
    IUpdateDepositTransactionStartPayload,
} from './Deposit.t';
import {
    getAppUser,
    getIsUserBuyerSelector,
    getUserEmailSelector,
    getUserPaymentAccountCode,
    getUserSelectedAccountCode,
} from '../../app/appSelectors';
import { getFormDataFieldDefaultValue } from '../Payment/paymentUtils';
import {
    checkBonusPromocodeSuccess,
    clearBonusPromocodeAction,
    setBonusPromocodeAction
} from '../Bonus/bonusesActions';
import {
    deleteDepositFormUserDataAction,
    deleteDepositFormUserDataFailure,
    deleteDepositFormUserDataSuccess,
    depositCreateAction,
    depositCreateFailure,
    depositCreateSuccess,
    depositInitAction,
    getDepositAvailableMethodsAction,
    getDepositAvailableMethodsFailure,
    getDepositAvailableMethodsSuccess,
    getDepositFormAction,
    getDepositFormDataListAction,
    getDepositFormDataListFailure,
    getDepositFormDataListSuccess,
    getDepositFormFailure,
    getDepositFormSuccess,
    getDepositFormUserDataAction,
    getDepositFormUserDataFailure,
    getDepositFormUserDataSuccess,
    getDepositParamsAction,
    getDepositParamsFailure,
    getDepositParamsSuccess,
    getDepositSurveyFailure,
    getDepositSurveySuccess,
    handleDepositFormUnmountAction,
    initDepositPaymentFormAction,
    sendDepositSurveyResultFailure,
    sendDepositSurveyResultSuccess,
    setDepositFormUserDataAction,
    setDepositFormUserDataFailure,
    setDepositFormUserDataSuccess,
    setDepositStepAction,
    setIsCheckDepositSurvey,
    updateDepositTransactionAction,
    updateDepositTransactionFailure,
    updateDepositTransactionSuccess,
} from './depositReducer';
import { IPaymentForm } from '../Payment/payment.t';
import { IGetDepositFormPayload } from '../Account/types/Account.t';
import { isClientAndroidNative } from '../../components/ClientInfo/ClientInfo';
import goToUrlByForm from '../../utils/goToUrlByForm';
import { getIsCheckDepositSurvey } from './depositSelectors';
import { DEPOSIT_STEPS } from './depositConstants';
import { pushGtmEventAction } from "../GoogleTagManager/googleTagManagerActions";
import { GTM_EVENTS } from "../GoogleTagManager/googleTagManagerConstants";
import { sendFacebookEventAction } from "../../app/appActions";
import { FB_EVENTS } from "../../utils/sendFacebookEvent";
import { locationSlice } from "../Location/locationReducer";
import { LOCATION_NAMES } from "../Location/locationConstants";

export function* getDepositFormDataListSaga() {
    try {
        const data: IDepositFormDataListItem[] = yield call(getRequest, '/deposit/form/list');

        yield put(getDepositFormDataListSuccess(data));
        return data;
    } catch (e: any) {
        yield call(failurePut, e, getDepositFormDataListFailure);
    }
}

export function* getDepositFormUserDataSaga({ payload }: { payload: IGetDepositFormUserDataActionPayload }) {
    try {
        const data: IDepositFormUserDataResponse = yield call(getRequest, `/deposit/form/${payload.form_id}`);

        yield put(getDepositFormUserDataSuccess(data));
        return data;
    } catch (e: any) {
        yield call(failurePut, e, getDepositFormUserDataFailure);
    }
}

export function* setDepositFormUserDataSaga({ payload }: { payload: ISetDepositFormUserDataActionPayload }) {
    try {
        const data: IDepositFormUserDataResponse = yield call(postRequest, `/deposit/form`, {
            form_id: payload.form_id,
            form_data: payload.form_data,
            payment_key: payload.payment_key,
        });
        yield put(setDepositFormUserDataSuccess(data));
        yield put(getDepositFormDataListAction());
        payload.cb && payload.cb(data);
    } catch (e: any) {
        payload.errorCb && payload.errorCb(e);
        yield call(failurePut, e, setDepositFormUserDataFailure);
    }
}

export function* deleteDepositFormUserDataSaga({ payload }: { payload: IDeleteDepositFormUserDataActionPayload }) {
    try {
        yield call(deleteRequest, `/deposit/form/${payload.form_id}`);
        yield put(deleteDepositFormUserDataSuccess());

        yield put(getDepositFormDataListAction());
        if (payload.cb) {
            yield call(payload.cb);
        }
    } catch (e: any) {
        yield call(failurePut, e, deleteDepositFormUserDataFailure);
    }
}

export function* tryUploadDepositFilledFormDataFromStorageSaga({ payload }: { payload: IDepositAvailableMethod }) {
    const accountCode = yield select(getUserSelectedAccountCode);
    const userEmail = yield select(getUserEmailSelector);
    const depositForm = yield call(getDepositFormSaga, { payload: { payment_key: payload.key } });
    if (!depositForm?.length) {
        return;
    }

    const formData = {};
    depositForm.forEach(item => {
        const newValue = getFormDataFieldDefaultValue({
            accountCode,
            formDataItem: item,
            userEmail,
        });
        if (newValue) {
            formData[item.field] = { field: item.field, value: newValue };
        }
    });
    yield call(setDepositFormUserDataSaga, {
        payload: {
            form_id: null,
            form_data: formData,
            payment_key: payload.key,
        },
    });
}

export function* initDepositPaymentFormSaga({ payload }: { payload: IDepositAvailableMethod }) {
    const formDataList = yield call(getDepositFormDataListSaga) || [];
    if (!formDataList?.length) {
        yield call(tryUploadDepositFilledFormDataFromStorageSaga, { payload });
    }
}

export function* handleDepositFormUnmountSaga() {
    yield put(setBonusPromocodeAction(''));
}

export function* getDepositParamsSaga() {
    try {
        const data: IDepositParams = yield call(getRequest, '/deposit');

        yield put(getDepositParamsSuccess(data));
    } catch (e: any) {
        yield call(failurePut, e, getDepositParamsFailure);
    }
}

export function* depositGetAvailableMethodsSaga({ payload }: { payload: IGetDepositAvailableMethodsActionPayload }) {
    try {
        const data: IDepositAvailableMethods = yield call(postRequest, '/deposit/available_methods', {
            country: payload.country,
        });
        yield put(getDepositAvailableMethodsSuccess(data));
    } catch (e: any) {
        if (e.response?.data?.messageCode === 'error.kyc_required') {
            yield put(locationSlice.actions.openLocationAction({ name: LOCATION_NAMES.VERIFY, method: 'replace' }));
        }
        yield call(failurePut, e, getDepositAvailableMethodsFailure);
    }
}

export function* getDepositFormSaga({ payload }: { payload: IGetDepositFormPayload }) {
    try {
        const data: IPaymentForm = yield call(getRequest, `/deposit/form?payment=${payload.payment_key}`);

        yield put(getDepositFormSuccess(data));
        return data;
    } catch (e: any) {
        yield call(failurePut, e, getDepositFormFailure);
    }
}

export function* depositCreateSaga({ payload }: { payload: IDepositCreateActionPayload }) {
    let depositWindow: Window;
    if (payload.paymentType === 'redirect' && !isClientAndroidNative) {
        depositWindow = window.open(`${document.location.protocol}//${document.location.host}/redirect-billing`);
    }
    try {
        const response: IDepositCreateSuccessPayload = yield call(postRequest, '/deposit/create', {
            accept_policy: payload.accept_policy,
            amount: payload.amount,
            apply_bonus: payload.apply_bonus,
            currency: payload.currency,
            form_data: payload.form_data,
            payment_id: payload.payment_id,
            payment_key: payload.payment_key,
            payment_name: payload.payment_name,
            promocode: payload.promocode,
        });
        yield put(depositCreateSuccess({
            ...payload,
            ...response,
            amount: payload.amount * 100,
        }));
        yield put(clearBonusPromocodeAction());
        if (response?.qr_code_picture) {
            // nothing
        } else if (response?.url) {
            if (isClientAndroidNative) {
                depositWindow = window.open();
            }
            if (response?.params) {
                goToUrlByForm({
                    currentWindow: depositWindow,
                    method: 'post',
                    params: response.params,
                    url: response.url,
                });
            } else {
                if (response.url.indexOf('//') === -1) {
                    response.url = 'https://' + response?.url;
                }
                depositWindow?.location?.assign && depositWindow.location.assign(response?.url);
            }
            payload.onRedirectCallback && payload.onRedirectCallback();
            yield put(checkBonusPromocodeSuccess(''));
        } else {
            if (depositWindow) {
                depositWindow.close();
            }
            yield put(depositCreateFailure('error.default_message'));
        }
        yield put(setIsCheckDepositSurvey(true));
        yield put(setDepositStepAction(DEPOSIT_STEPS.PAYMENT_DETAILS));
        yield put(pushGtmEventAction({ event: GTM_EVENTS.DEPOSIT_CREATED }));
        payload.onSuccessCallback && payload.onSuccessCallback();
    } catch (e: any) {
        if (depositWindow) {
            depositWindow.close();
        }
        yield call(failurePut, e, depositCreateFailure);
    }
}

export function* depositCreateSuccessSaga({ payload }: { payload: IDepositCreateSuccessPayload }) {
    const userProfile = yield select(getAppUser);
    yield put(sendFacebookEventAction({
        event: FB_EVENTS.PAYMENT_DEPOSIT_CREATED,
        eventID: `deposit_created_${userProfile.uid}`,
        data: {
            currency: payload.currency,
            value: payload.amount,
        },
    }));
}

export function* updateDepositTransactionSaga({ payload }: { payload: IUpdateDepositTransactionStartPayload }) {
    try {
        const accountCode: string = yield select(getUserPaymentAccountCode);
        const encodedAccountCode: string = encodeURIComponent(accountCode);
        const data = yield call(getRequest, `/account/${encodedAccountCode}/history/transaction/${payload.uuid}`);
        yield put(updateDepositTransactionSuccess(data));
        payload.onSuccessCallback && payload.onSuccessCallback();
    } catch (e: any) {
        yield call(failurePut, e, updateDepositTransactionFailure);
    }
}

export function* getDepositSurveySaga({ payload }: { payload: IGetDepositSurveyActionPayload }) {
    const isUserBuyer: boolean = yield select(getIsUserBuyerSelector);
    const isCheckDepositSurvey: boolean = yield select(getIsCheckDepositSurvey);
    yield put(setIsCheckDepositSurvey(false));

    if (!isCheckDepositSurvey || isUserBuyer) {
        return;
    }

    try {
        const data: { show_deposit_survey: boolean } = yield call(getRequest, '/deposit/survey');
        if (data.show_deposit_survey) {
            payload.onShowDepositSurvey && payload.onShowDepositSurvey();
        }
        yield put(getDepositSurveySuccess(data));
    } catch (e: any) {
        yield call(failurePut, e, getDepositSurveyFailure);
    }
}

export function* sendDepositSurveyResultSaga({ payload }: { payload: ISendDepositSurveyActionPayload }) {
    try {
        yield call(postRequest, '/deposit/survey', payload);
        yield put(sendDepositSurveyResultSuccess());
    } catch (e: any) {
        yield call(failurePut, e, sendDepositSurveyResultFailure);
    }
}

export function* depositInitSaga() {
    yield call(getDepositParamsSaga);
    const userProfile = yield select(getAppUser);
    yield put(sendFacebookEventAction({
        event: FB_EVENTS.PAYMENT_OPEN,
        eventID: `deposit_open_${userProfile.uid}`,
        data: {},
    }));
}

export function* depositSagas() {
    yield takeEvery(depositInitAction, depositInitSaga);
    yield takeEvery(getDepositFormDataListAction, getDepositFormDataListSaga);
    yield takeEvery(getDepositFormUserDataAction, getDepositFormUserDataSaga);
    yield takeEvery(setDepositFormUserDataAction, setDepositFormUserDataSaga);
    yield takeEvery(deleteDepositFormUserDataAction, deleteDepositFormUserDataSaga);
    yield takeEvery(initDepositPaymentFormAction, initDepositPaymentFormSaga);
    yield takeEvery(handleDepositFormUnmountAction, handleDepositFormUnmountSaga);
    yield takeEvery(getDepositParamsAction, getDepositParamsSaga);
    yield takeEvery(getDepositAvailableMethodsAction, depositGetAvailableMethodsSaga);
    yield takeEvery(getDepositFormAction, getDepositFormSaga);
    yield takeEvery(depositCreateAction, depositCreateSaga);
    yield takeEvery(depositCreateSuccess, depositCreateSuccessSaga);
    yield takeEvery(updateDepositTransactionAction, updateDepositTransactionSaga);
}
